import React from 'react';
import { Header } from '../components/Header';
import { ContactSection } from '../components/ContactSection';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { 
  MessageSquare, 
  Shield, 
  Zap, 
  FileText,
  Target,
  Users,
  Award,
  Mail,
  Github,
  Twitter,
  Linkedin,
  Heart,
  Globe
} from 'lucide-react';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleHome = () => {
    navigate('/');
  };

  const handleNewChat = () => {
    navigate('/');
  };

  // 团队成员
  const teamMembers = [
    {
      name: t('张明', 'Ming Zhang'),
      role: t('创始人 & AI专家', 'Founder & AI Specialist'),
      bio: t('前英国移民局技术顾问，10年AI领域经验', 'Former UK immigration technology consultant with 10 years of AI experience.'),
      avatar: '👨‍💻'
    },
    {
      name: t('李华', 'Hua Li'),
      role: t('移民法律顾问', 'Immigration Legal Advisor'),
      bio: t('英国注册移民律师，处理过1000+签证案例', 'UK registered immigration lawyer with 1,000+ visa cases handled.'),
      avatar: '👩‍⚖️'
    },
    {
      name: t('王伟', 'Wei Wang'),
      role: t('全栈开发工程师', 'Full-stack Developer'),
      bio: t('前谷歌工程师，擅长AI应用开发', 'Former Google engineer specialising in AI application development.'),
      avatar: '👨‍💻'
    },
    {
      name: t('陈思', 'Si Chen'),
      role: t('产品设计师', 'Product Designer'),
      bio: t('专注于用户体验设计，让复杂变简单', 'Focused on user experience design that makes complex workflows simple.'),
      avatar: '🎨'
    }
  ];

  // 发展历程
  const milestones = [
    {
      year: '2023',
      title: t('公司成立', 'Founded'),
      description: t('在英国伦敦创立，专注于AI+移民科技', 'Founded in London, focused on AI-powered immigration technology.')
    },
    {
      year: '2024',
      title: t('产品上线', 'Product Launch'),
      description: t('英国签证AI助手正式发布，服务1000+用户', 'UK Visa AI Assistant launched and served 1,000+ users.')
    },
    {
      year: '2025',
      title: t('获得投资', 'Funding Secured'),
      description: t('获得500万天使轮融资，扩大团队规模', 'Raised 5 million in angel funding and expanded the team.')
    },
    {
      year: '2026',
      title: t('全球拓展', 'Global Expansion'),
      description: t('计划拓展到美国、加拿大、澳大利亚市场', 'Planning expansion into the US, Canada, and Australia.')
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f172a]">
      <Header onClearChat={handleNewChat} onHome={handleHome} showNewChat={true} />

      {/* Hero 区域 */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 dark:from-indigo-900 dark:via-indigo-800 dark:to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">{t('关于我们', 'About Us')}</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            {t('让英国签证申请变得更简单、更智能', 'Making UK visa applications simpler and smarter.')}
          </p>
        </div>
      </div>

      {/* 使命愿景 */}
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-[#1e293b] rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('我们的使命', 'Our Mission')}</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('利用先进的AI技术，为全球用户提供准确、及时、个性化的英国签证申请指导，让复杂的签证流程变得简单易懂，帮助更多人实现赴英梦想。', 'Use advanced AI to provide accurate, timely, and personalised UK visa guidance, making complex processes easier to understand and helping more people achieve their UK plans.')}
            </p>
          </div>

          <div className="bg-white dark:bg-[#1e293b] rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4">
              <Award className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('我们的愿景', 'Our Vision')}</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('成为全球领先的AI移民科技公司，打造最智能、最可靠的签证申请助手，服务覆盖全球主要移民国家，让移民申请不再困难。', 'Become a leading AI immigration technology company and build the most intelligent, reliable visa assistant for major immigration destinations worldwide.')}
            </p>
          </div>
        </div>
      </div>

      {/* 核心价值 */}
      <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('核心价值', 'Core Values')}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('准确可靠', 'Accurate and reliable')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('基于UKVI官方文档，确保信息准确', 'Based on official UKVI documents for reliable information.')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('即时响应', 'Instant responses')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('7x24小时在线，随时随地解答问题', '24/7 online answers whenever you need them.')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('全球服务', 'Global service')}</h3>
              <p className="text-gray-600 dark:text-gray-400">{t('支持多语言，服务全球用户', 'Multilingual support for users around the world.')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 团队介绍 */}
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('核心团队', 'Core Team')}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, index) => (
            <div key={index} className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 text-center hover:shadow-md transition-shadow">
              <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
                {member.avatar}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{member.name}</h3>
              <p className="text-sm text-indigo-600 dark:text-indigo-400 mb-2">{member.role}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{member.bio}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 发展历程 */}
      <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('发展历程', 'Timeline')}</h2>
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{milestone.year}</span>
                </div>
                <div className="flex-shrink-0 relative">
                  <div className="w-4 h-4 bg-indigo-600 rounded-full mt-2"></div>
                  {index < milestones.length - 1 && (
                    <div className="absolute top-6 left-2 w-0.5 h-16 bg-indigo-200 dark:bg-indigo-800"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{milestone.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 联系我们 */}
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <ContactSection title={t('联系我们', 'Contact Us')} />
      </div>

      {/* 底部版权 */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-400">
            <p>{t('© 2026 英国签证AI助手. 保留所有权利。', '© 2026 UK Visa AI Assistant. All rights reserved.')}</p>
            <p className="mt-2">
              <a href="#" className="hover:text-white transition-colors">{t('ICP证: 京ICP备12345678号', 'ICP: Beijing ICP 12345678')}</a> | 
              <a href="#" className="hover:text-white transition-colors ml-2">{t('营业执照', 'Business License')}</a> | 
              <a href="#" className="hover:text-white transition-colors ml-2">{t('隐私政策', 'Privacy Policy')}</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
